/** Orb animasi Jenna — konstanta & tipe dipakai bersama server dan klien. */

/**
 * Varian Canvas 2D asli (T1). `orbit-dots` dan `liquid-blob` dirancang tapi
 * ditunda; lihat docs/ai-assistant/ORB-LOGO-PROPOSAL.md §3.
 */
export const CANVAS2D_ORB_VARIANTS = [
	"particle-sphere",
	"wireframe-mesh",
	"glow-ring",
] as const;

/**
 * Varian WebGL2 (T5) — "liquid glass" terinspirasi ref-4. Warna BUKAN palet
 * tersendiri: diturunkan dari colorFrom/colorTo admin (lihat
 * deriveLiquidGlassStops di orb-webgl-palette.ts). Kedua varian beda GAYA
 * SHADING saja (distorsi/kecepatan/kilau), bukan warna tetap.
 */
export const WEBGL_ORB_VARIANTS = [
	"liquid-glass-flow",
	"liquid-glass-dramatic",
	"liquid-morph",
] as const;

/**
 * Varian orb yang tersedia. Disimpan di DB sebagai String bebas (bukan enum
 * Prisma), jadi menambah varian baru cukup di sini — tanpa migrasi.
 */
export const ORB_VARIANTS = [
	...CANVAS2D_ORB_VARIANTS,
	...WEBGL_ORB_VARIANTS,
] as const;
export type OrbVariant = (typeof ORB_VARIANTS)[number];
export type WebglOrbVariant = (typeof WEBGL_ORB_VARIANTS)[number];

export const DEFAULT_ORB_VARIANT: OrbVariant = "particle-sphere";

export function isOrbVariant(value: unknown): value is OrbVariant {
	return (
		typeof value === "string" &&
		(ORB_VARIANTS as readonly string[]).includes(value)
	);
}

/**
 * true bila varian dirender lewat WebGL2 (satu konteks bersama, lihat
 * orb-webgl-stage.ts), bukan Canvas 2D. Dipakai komponen untuk memilih jalur
 * render, dan oleh jalur fallback untuk tahu kapan harus turun ke Canvas 2D.
 */
/**
 * Varian yang palet warnanya TETAP (spektrum prisma, keputusan user) dan tidak
 * mengikuti colorFrom/colorTo/followTheme. Admin menonaktifkan pemilih warna
 * untuk varian ini supaya tidak ada kontrol yang tampak berfungsi tapi tidak
 * berpengaruh; kolom warna tetap ada di model & validasi (tanpa migrasi).
 */
export const FIXED_PALETTE_ORB_VARIANTS: readonly OrbVariant[] = [
	"liquid-morph",
];

export function hasFixedPalette(variant: OrbVariant): boolean {
	return FIXED_PALETTE_ORB_VARIANTS.includes(variant);
}

export function isWebglOrbVariant(
	variant: OrbVariant,
): variant is WebglOrbVariant {
	return (WEBGL_ORB_VARIANTS as readonly string[]).includes(variant);
}

/**
 * Ukuran orb — menentukan sisi tombol FAB dan logo header. Enum, bukan angka
 * bebas: admin memilih tiga langkah yang semuanya aman untuk keterbacaan (sisi
 * header terkecil tetap ≥ 24px sehingga varian partikel/jala tidak otomatis
 * jatuh ke cincin). Pemetaan ke piksel ada di orb.config.ts (konstanta bernama).
 */
export const ORB_SIZES = ["kecil", "sedang", "besar"] as const;
export type OrbSize = (typeof ORB_SIZES)[number];

/** Bawaan "sedang": lebih besar & enak dilihat dari tampilan lama, tapi belum semaksimal "besar". */
export const DEFAULT_ORB_SIZE: OrbSize = "sedang";

export function isOrbSize(value: unknown): value is OrbSize {
	return (
		typeof value === "string" &&
		(ORB_SIZES as readonly string[]).includes(value)
	);
}

/**
 * Warna hanya boleh hex 6 digit. Nilainya berakhir di `ctx.fillStyle` dan
 * `style`, jadi string CSS bebas tidak diterima — dicek di server & klien.
 */
export const ORB_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;

export function isOrbColor(value: unknown): value is string {
	return typeof value === "string" && ORB_COLOR_PATTERN.test(value);
}

/** Jumlah slot parameter per varian; tetap 4 agar schema tidak berubah saat varian bertambah. */
export const ORB_PARAM_SLOTS = 4;
export type OrbParams = readonly [number, number, number, number];

/** Satu slider di UI admin: arti slot ke-n pada sebuah varian. */
export interface OrbParamSpec {
	/** Nama slot, dipakai renderer untuk membaca nilainya. */
	readonly key: string;
	/** Kunci teks i18n (bukan teks mentah). */
	readonly labelKey: string;
	readonly min: number;
	readonly max: number;
	readonly default: number;
}

/**
 * Arti keempat slot parameter untuk tiap varian — satu-satunya sumber rentang
 * slider admin dan nilai bawaan renderer (rule 15: tidak ada angka tersebar).
 */
export const ORB_VARIANT_SPECS: Record<
	OrbVariant,
	readonly OrbParamSpec[] // 0–4 slot; varian tanpa parameter (liquid-morph) memakai []
> = {
	"particle-sphere": [
		{ key: "density", labelKey: "density", min: 400, max: 3000, default: 1600 },
		{ key: "speed", labelKey: "speed", min: 0, max: 100, default: 35 },
		{ key: "glow", labelKey: "glow", min: 0, max: 100, default: 45 },
		{ key: "tilt", labelKey: "tilt", min: 0, max: 45, default: 18 },
	],
	"wireframe-mesh": [
		{ key: "density", labelKey: "meshDensity", min: 8, max: 40, default: 20 },
		{ key: "amplitude", labelKey: "amplitude", min: 0, max: 100, default: 40 },
		{ key: "speed", labelKey: "speed", min: 0, max: 100, default: 30 },
		{ key: "glow", labelKey: "glow", min: 0, max: 100, default: 55 },
	],
	"glow-ring": [
		{ key: "thickness", labelKey: "thickness", min: 1, max: 20, default: 6 },
		{ key: "speed", labelKey: "pulseSpeed", min: 0, max: 100, default: 25 },
		{ key: "glow", labelKey: "glow", min: 0, max: 100, default: 60 },
		{ key: "core", labelKey: "core", min: 0, max: 100, default: 50 },
	],
	// Rentang integer di sini dibagi 100 di sisi shader (lihat
	// toLiquidGlassUniforms di orb-webgl-palette.ts) untuk dapat pecahan GLSL
	// (mis. distortion 40 → uDistortion 0.4) — param DB tetap Int sesuai desain
	// T2, tak ada kolom baru.
	"liquid-glass-flow": [
		{
			key: "distortion",
			labelKey: "lgDistortion",
			min: 0,
			max: 100,
			default: 40,
		},
		{ key: "speed", labelKey: "speed", min: 0, max: 150, default: 85 },
		{ key: "flow", labelKey: "lgFlowScale", min: 100, max: 400, default: 200 },
		{ key: "sparkle", labelKey: "lgSparkle", min: 0, max: 200, default: 90 },
	],
	"liquid-glass-dramatic": [
		{
			key: "distortion",
			labelKey: "lgDistortion",
			min: 0,
			max: 100,
			default: 62,
		},
		{ key: "speed", labelKey: "speed", min: 0, max: 150, default: 110 },
		{ key: "flow", labelKey: "lgFlowScale", min: 100, max: 400, default: 280 },
		{ key: "sparkle", labelKey: "lgSparkle", min: 0, max: 200, default: 130 },
	],
	// Tanpa parameter: bentuk, kecepatan siklus, dan palet mengikuti prototipe
	// yang disetujui user (palet tetap). Slot DB tetap ada; nilainya 0 dan tak dibaca.
	"liquid-morph": [],
};

/** Warna bawaan = preset "Darmasaba" (darmasaba-navy[7] → darmasaba-blue[4]). */
export const DEFAULT_ORB_COLOR_FROM = "#1E3766";
export const DEFAULT_ORB_COLOR_TO = "#3B8FFF";

/** Preset warna, seluruhnya diambil dari palet dashboard yang sudah ada. */
export const ORB_COLOR_PRESETS = [
	{ id: "darmasaba", from: DEFAULT_ORB_COLOR_FROM, to: DEFAULT_ORB_COLOR_TO },
	{ id: "samudra", from: "#12264D", to: "#5C9DB8" },
	{ id: "senja", from: "#7060B8", to: "#D89A3C" },
	{ id: "zamrud", from: "#12264D", to: "#3E9B6B" },
	{ id: "bara", from: "#7060B8", to: "#D14D4D" },
	{ id: "monokrom", from: "#364356", to: "#DCE3EC" },
] as const;

/** Konfigurasi orb yang dikirim ke klien (ikut di dalam status asisten). */
export interface AssistantOrbConfig {
	variant: OrbVariant;
	size: OrbSize;
	colorFrom: string;
	colorTo: string;
	/** true = abaikan colorFrom/colorTo, pakai warna tema dashboard. */
	followTheme: boolean;
	params: OrbParams;
}

/** Nilai bawaan tiap slot untuk sebuah varian. */
export function defaultOrbParams(variant: OrbVariant): OrbParams {
	const specs = ORB_VARIANT_SPECS[variant];
	const at = (i: number): number => specs[i]?.default ?? 0;
	return [at(0), at(1), at(2), at(3)];
}

export function defaultOrbConfig(): AssistantOrbConfig {
	return {
		variant: DEFAULT_ORB_VARIANT,
		size: DEFAULT_ORB_SIZE,
		colorFrom: DEFAULT_ORB_COLOR_FROM,
		colorTo: DEFAULT_ORB_COLOR_TO,
		followTheme: false,
		params: defaultOrbParams(DEFAULT_ORB_VARIANT),
	};
}

/**
 * Jepit tiap slot ke rentang varian-nya. Dipakai saat menyimpan, supaya nilai
 * di DB selalu masuk akal walau klien mengirim angka liar — dan supaya rentang
 * yang berubah di versi berikutnya tidak membuat baris lama tak terpakai.
 */
export function clampOrbParams(
	variant: OrbVariant,
	params: OrbParams,
): OrbParams {
	const specs = ORB_VARIANT_SPECS[variant];
	const at = (i: number): number => {
		const spec = specs[i];
		if (!spec) return 0; // slot yang tak dipakai varian ini
		const raw = params[i];
		const value = Number.isFinite(raw)
			? Math.trunc(raw as number)
			: spec.default;
		return Math.min(spec.max, Math.max(spec.min, value));
	};
	return [at(0), at(1), at(2), at(3)];
}

/**
 * Slot parameter dalam bentuk bernama agar renderer tidak membaca `params[2]`.
 * Nilai di luar rentang spec dijepit, bukan ditolak — konfigurasi lama tetap
 * bisa dirender walau rentangnya berubah di versi berikutnya.
 */
export function toOrbParamRecord(
	variant: OrbVariant,
	params: OrbParams,
): Readonly<Record<string, number>> {
	const specs = ORB_VARIANT_SPECS[variant];
	const record: Record<string, number> = {};
	for (let i = 0; i < ORB_PARAM_SLOTS; i++) {
		const spec = specs[i];
		if (!spec) continue;
		const raw = params[i];
		const value = Number.isFinite(raw) ? (raw as number) : spec.default;
		record[spec.key] = Math.min(spec.max, Math.max(spec.min, value));
	}
	return record;
}
